import { RadioGroupOption } from '@types';
import clsx from 'clsx';
import React, { forwardRef, InputHTMLAttributes, LegacyRef, useEffect, useState } from 'react';

import styles from './RadioButton.module.scss';

interface RadioElementProps<TKey> {
  option: RadioGroupOption<TKey>;
  selected?: boolean;
  isChecked?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { ref?: React.ForwardedRef<HTMLInputElement> };
  id?: any;
  ref?: LegacyRef<HTMLLabelElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** When true, renders only the styled radio circle without the label wrapper or text. */
  radioOnly?: boolean;
}

export const RadioElement = forwardRef(
  <TKey,>(
    { option, inputProps, isChecked, id, onChange, radioOnly }: RadioElementProps<TKey>,
    ref: LegacyRef<HTMLLabelElement>,
  ) => {
    const [checked, setChecked] = useState(false);

    useEffect(() => {
      setChecked(isChecked);
    }, [isChecked]);

    if (radioOnly) {
      return (
        <label
          className={clsx(styles.radioOnly, { [styles.checked]: checked, [styles.disabled]: option.disabled })}
          onClick={e => e.stopPropagation()}
        >
          <input
            type="radio"
            checked={checked}
            className={styles.input}
            onChange={onChange}
            disabled={option.disabled}
            {...inputProps}
          />
          <div className={styles.radio}>
            <div className={styles.inner} />
          </div>
        </label>
      );
    }

    return (
      <label
        className={clsx(styles.wrapper, { [styles.checked]: checked, [styles.disabled]: option.disabled })}
        id={id}
        ref={ref}
        data-testid={`radio-option-${inputProps?.value}`}
      >
        <input
          {...inputProps}
          type="radio"
          checked={checked}
          className={styles.input}
          onChange={onChange}
          disabled={option.disabled}
        />
        <div className={styles.radio}>
          <div className={styles.inner} />
        </div>
        {option.name?.length > 0 && (
          <div className={styles.labelWrapper}>
            <p className={styles.label}>{option.name}</p>
            {option.icon && <div className={styles.icon}>{option.icon}</div>}
          </div>
        )}
      </label>
    );
  },
);
